import { Search, X } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';

interface TaskSearchBarProps {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  /** Number of matching tasks, shown inline while a query is active. */
  resultCount?: number;
  className?: string;
}

/**
 * Free-text task search box shared by the List view and the "My Tasks" board.
 * Purely presentational — the owning page holds the query state and applies the
 * {@link ../lib/taskSearch} matcher to its own task set.
 */
export function TaskSearchBar({
  value,
  onChange,
  placeholder = 'Search tasks…',
  resultCount,
  className,
}: TaskSearchBarProps) {
  const active = value.trim().length > 0;
  return (
    <div className={cn('relative w-full max-w-xs', className)}>
      <Search
        className="pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground"
        aria-hidden="true"
      />
      <Input
        type="search"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        aria-label="Search tasks"
        className="h-9 pl-8 pr-16"
      />
      {active && (
        <div className="absolute right-1 top-1/2 flex -translate-y-1/2 items-center gap-1">
          {resultCount !== undefined && (
            <span className="text-xs tabular-nums text-muted-foreground" aria-live="polite">
              {resultCount}
            </span>
          )}
          <Button
            type="button"
            variant="ghost"
            size="icon"
            className="h-6 w-6 flex-shrink-0"
            onClick={() => onChange('')}
            aria-label="Clear search"
          >
            <X className="h-3.5 w-3.5" />
          </Button>
        </div>
      )}
    </div>
  );
}
